import { BehaviorSubject } from 'rxjs';
import { ValidationType, ValidationResultsVm } from './validation-results.model';
import { ClassificationResult } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';

export class ValidationResultsPresenter {
  /**
   * Feature flag to control privacy warnings display
   * Set to false to hide privacy warnings (faces, text detection) when API is unreliable
   * Set to true to show privacy warnings when API improves
   */
  private readonly SHOW_PRIVACY_WARNINGS = false;

  vm: ValidationResultsVm;
  subject$: BehaviorSubject<ValidationResultsVm>;
  private i18ns: I18nKeys;

  setI18ns(i18ns: I18nKeys): void {
    this.i18ns = i18ns;
  }

  /**
   * Interpolate %varname% placeholders in a string with provided values
   */
  private interpolate(template: string, params: Record<string, string | number>): string {
    return template.replace(/%(\w+)%/g, (_, key) => String(params[key] ?? ''));
  }

  load(
    vmSubject$: BehaviorSubject<ValidationResultsVm>,
    results: ClassificationResult,
    type: string,
    inProgress: boolean
  ): void {
    this.subject$ = vmSubject$;

    const validationType = this.determineValidationType(results, inProgress);
    const equipmentType = this.mapTypeClassification(type);

    // Check if there's a real equipment detection (not empty, null, undefined, or 'Unknown')
    const hasDetections = !!(
      results?.equipmentType &&
      results.equipmentType !== '' &&
      results.equipmentType !== 'Unknown'
    );

    const reassuranceMessage = this.getReassuranceMessage(validationType);

    const filteredWarnings = this.filterPrivacyWarnings(results?.warnings || []);

    this.vm = {
      validationType,
      success: results?.success || inProgress,
      message: this.buildMessage(results, equipmentType, validationType),
      blockColor: this.getBlockColor(validationType),
      iconName: this.getIconName(validationType),
      title: this.getTitle(validationType),
      qualityIssues: this.stripTechnicalDetails(results?.qualityIssues || []),
      warnings: this.stripTechnicalDetails(this.transformConfidenceWarnings(filteredWarnings, results)),
      hasDetections,
      confidencePercentage: results?.confidence || 0,
      reassuranceMessage,
      qualityIssuesSectionTitle: (this.i18ns?.aiValidation?.qualityIssuesLabel || 'Quality Issues') + ':',
      warningsSectionTitle: (this.i18ns?.aiValidation?.detectionIssuesLabel || 'Detection Issues') + ':',
      showQualityIssues: (results?.qualityIssues?.length ?? 0) > 0,
      showWarnings:
        filteredWarnings.length > 0 && validationType !== 'quality_issues' && validationType !== 'no_detection',
      showReassuranceMessage: !!reassuranceMessage,
      inProgress
    };

    this.subject$.next(this.vm);
  }

  private determineValidationType(result: ClassificationResult, inProgress: boolean): ValidationType {
    // Show loading state if in progress or no result yet
    if (inProgress || !result) {
      return 'in_progress';
    }

    // Check for error status
    if (result.status === 'error') {
      return 'error';
    }

    // Quality issues take priority
    if (!result.qualityAcceptable && result.qualityIssues?.length > 0) {
      return 'quality_issues';
    }

    // Success - must check before no detection/low confidence
    if (result.success && result.confidenceThresholdMet) {
      return 'success';
    }

    // No detection (high quality but no equipment found) - check before low confidence
    // Empty string, undefined, null, or 'Unknown' equipmentType means nothing was detected
    const noDetection = !result.equipmentType || result.equipmentType === '' || result.equipmentType === 'Unknown';

    if (result.qualityAcceptable && noDetection) {
      return 'no_detection';
    }

    // Low confidence (detection found but below threshold)
    // Only if there's actually a real equipment type detected
    if (result.qualityAcceptable && !result.confidenceThresholdMet && !noDetection) {
      return 'low_confidence';
    }

    // Default to error for unexpected states
    return 'error';
  }

  private buildMessage(result: ClassificationResult, equipmentType: string, validationType: ValidationType): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (validationType) {
      case 'in_progress':
        return aiI18n?.qualityCheckingImage
          ? this.interpolate(aiI18n.qualityCheckingImage, { equipmentType })
          : `Quality checking ${equipmentType} image...`;

      case 'success':
        return aiI18n?.equipmentFound
          ? this.interpolate(aiI18n.equipmentFound, { equipmentType })
          : `${equipmentType} equipment found.`;

      case 'quality_issues':
        return aiI18n?.qualityTooLowMessage || 'Image quality is too low for reliable classification. Please retake the photo with better quality.';

      case 'low_confidence':
        return aiI18n?.lowConfidenceMessage || "We have detected your equipment but we're not completely confident. For better results please retake the photo.";

      case 'no_detection':
        return aiI18n?.noEquipmentDetectedMessage
          ? this.interpolate(aiI18n.noEquipmentDetectedMessage, { equipmentType })
          : `No equipment detected in the image. Please ensure the ${equipmentType} is clearly visible and retake the photo.`;

      case 'error':
        return result?.error || (aiI18n?.validationFailedFor
          ? this.interpolate(aiI18n.validationFailedFor, { equipmentType })
          : `Validation failed for ${equipmentType} image.`);

      default:
        return aiI18n?.validationFailedFor
          ? this.interpolate(aiI18n.validationFailedFor, { equipmentType })
          : `Validation failed for ${equipmentType} image.`;
    }
  }

  private getBlockColor(validationType: ValidationType): 'success' | 'low' | 'warning' | 'danger' | 'info' {
    switch (validationType) {
      case 'success':
        return 'success';
      case 'quality_issues':
        return 'warning';
      case 'low_confidence':
        return 'warning';
      case 'no_detection':
        return 'info';
      case 'in_progress':
        return 'low';
      case 'error':
        return 'danger';
      default:
        return 'danger';
    }
  }

  private getIconName(validationType: ValidationType): 'psychology' | 'check' | 'error' | 'info' | 'warning' {
    switch (validationType) {
      case 'success':
        return 'check';
      case 'quality_issues':
        return 'error';
      case 'low_confidence':
        return 'warning';
      case 'no_detection':
        return 'info';
      case 'in_progress':
        return 'psychology';
      case 'error':
        return 'error';
      default:
        return 'error';
    }
  }

  private getTitle(validationType: ValidationType): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (validationType) {
      case 'success':
        return aiI18n?.imageValidationSuccessful || 'Image Validation Successful';
      case 'quality_issues':
        return aiI18n?.imageQualityIssues || 'Image Quality Issues';
      case 'low_confidence':
        return aiI18n?.lowConfidenceDetection || 'Low Confidence Detection';
      case 'no_detection':
        return aiI18n?.noEquipmentDetected || 'No Equipment Detected';
      case 'in_progress':
        return aiI18n?.imageValidationInProgress || 'Image Validation In Progress';
      case 'error':
        return aiI18n?.imageValidationFailed || 'Image Validation Failed';
      default:
        return aiI18n?.imageValidationFailed || 'Image Validation Failed';
    }
  }

  private mapTypeClassification(type: string): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (type) {
      case 'fuse-cutout':
        return aiI18n?.fuseCutout || 'Fuse Cutout';
      case 'consumer-unit':
        return aiI18n?.consumerUnit || 'Consumer Unit';
      case 'isolator-switch':
        return aiI18n?.isolatorSwitch || 'Isolator Switch';
      default:
        return aiI18n?.unknown || 'Unknown';
    }
  }

  /**
   * Strip technical details in parentheses from messages to make them more user-friendly
   * Example: "Low structural sharpness (brenner=0.00 < 0.30)" -> "Low structural sharpness"
   */
  private stripTechnicalDetails(messages: string[]): string[] {
    return messages.map(message => message.replace(/\s*\([^)]*\)/g, '').trim());
  }

  /**
   * Get reassurance message for quality-passed but detection-failed scenarios
   * Only shows for low_confidence and no_detection cases where the image quality is good
   */
  private getReassuranceMessage(validationType: ValidationType): string | undefined {
    if (validationType === 'low_confidence' || validationType === 'no_detection') {
      return this.i18ns?.aiValidation?.reassuranceMessage || 'You can still use this image if you think we have got this wrong.';
    }
    return undefined;
  }

  /**
   * Filter privacy-related warnings based on SHOW_PRIVACY_WARNINGS flag
   * Removes warnings about faces, text detection, and privacy risks when flag is false
   */
  private filterPrivacyWarnings(warnings: string[]): string[] {
    if (this.SHOW_PRIVACY_WARNINGS) {
      return warnings;
    }

    // Filter out privacy-related warnings
    const privacyKeywords = ['privacy', 'face', 'faces', 'text detected'];
    return warnings.filter(warning => {
      const lowercaseWarning = warning.toLowerCase();
      return !privacyKeywords.some(keyword => lowercaseWarning.includes(keyword));
    });
  }

  /**
   * Transform technical confidence warnings into user-friendly messages
   * Example: "Classification confidence below threshold: 46.4% < 50.0%"
   *       -> "We're only 46.4% confident of a Fuse Cutout in this image"
   */
  private transformConfidenceWarnings(warnings: string[], results: ClassificationResult | null): string[] {
    const aiI18n = this.i18ns?.aiValidation;

    return warnings.map(warning => {
      // Check if this is a confidence threshold warning
      const confidenceMatch = warning.match(/Classification confidence below threshold: ([\d.]+)%/);

      if (confidenceMatch && results?.equipmentType) {
        const confidence = confidenceMatch[1];
        const equipmentName = this.getEquipmentName(results.equipmentType);
        return aiI18n?.confidenceWarning
          ? this.interpolate(aiI18n.confidenceWarning, { confidence, equipmentName })
          : `We're only ${confidence}% confident there is a ${equipmentName} in this image`;
      }

      return warning;
    });
  }

  /**
   * Get user-friendly equipment name from API classification
   */
  private getEquipmentName(equipmentType: string): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (equipmentType) {
      case 'FuseCutOut':
        return aiI18n?.fuseCutout || 'Fuse Cutout';
      case 'ConsumerUnit':
        return aiI18n?.consumerUnit || 'Consumer Unit';
      case 'IsolatorSwitch':
        return aiI18n?.isolatorSwitch || 'Isolator Switch';
      default:
        return aiI18n?.equipment || 'equipment';
    }
  }
}
